"use client"

import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Download, Filter, ChevronDown } from "lucide-react"
import { CANDIDATE_CATEGORIES } from "@/utils/applicantsTable/constants"

interface TableControlsProps {
  showOnlyQualified: boolean
  onShowOnlyQualifiedChange: (checked: boolean) => void
  showHidden: boolean
  onShowHiddenChange: (checked: boolean) => void
  hiddenCount: number
  categoryFilter: string | null
  onCategoryFilterChange: (category: string | null) => void
  categoryCounts: Record<string, number>
  onDownloadCSV: () => void
}

export function TableControls({
  showOnlyQualified,
  onShowOnlyQualifiedChange,
  showHidden,
  onShowHiddenChange,
  hiddenCount,
  categoryFilter,
  onCategoryFilterChange,
  categoryCounts,
  onDownloadCSV,
}: TableControlsProps) {
  return (
    <div className="flex flex-wrap gap-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="bg-gray-800 text-white border-gray-700 hover:bg-gray-700">
            <Filter className="mr-2 h-4 w-4" /> Filter
            <ChevronDown className="ml-2 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56 bg-gray-800 text-gray-100 border-gray-700">
          <DropdownMenuLabel>Filter by Category</DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-gray-700" />
          <DropdownMenuGroup>
            <DropdownMenuItem
              onClick={() => onCategoryFilterChange(null)}
              className={!categoryFilter ? "bg-gray-700" : "hover:bg-gray-700"}
            >
              All Categories
            </DropdownMenuItem>
            {Object.entries(CANDIDATE_CATEGORIES).map(([category, info]) => (
              <DropdownMenuItem
                key={category}
                onClick={() => onCategoryFilterChange(category)}
                className={categoryFilter === category ? "bg-gray-700" : "hover:bg-gray-700"}
              >
                <div className="flex items-center w-full">
                  {info.icon}
                  <span className="ml-2">{category}</span>
                  <Badge className={`ml-auto ${info.badge}`}>{categoryCounts[category] || 0}</Badge>
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="flex items-center space-x-2 bg-gray-800 text-white px-3 py-2 rounded-md">
        <Switch id="qualified-filter" checked={showOnlyQualified} onCheckedChange={onShowOnlyQualifiedChange} />
        <Label htmlFor="qualified-filter" className="text-white cursor-pointer">
          Qualified only
        </Label>
      </div>

      <div className="flex items-center space-x-2 bg-gray-800 text-white px-3 py-2 rounded-md">
        <Switch id="show-hidden" checked={showHidden} onCheckedChange={onShowHiddenChange} />
        <Label htmlFor="show-hidden" className="text-white cursor-pointer">
          Show hidden ({hiddenCount})
        </Label>
      </div>

      <Button onClick={onDownloadCSV} className="bg-gray-800 text-white hover:bg-gray-700">
        <Download className="mr-2 h-4 w-4" /> Export CSV
      </Button>
    </div>
  )
}
